/**
 * AppShellContext — Shared state for the App Shell layout.
 *
 * Manages sidebar mode (full / icon / thin), activity bar position/state,
 * and shares the active patient's navigation and activity state.
 *
 * Phase 1: Sidebar mode, toggle, CSS variable sync.
 * Phase 2: Activity bar position, open/close, size presets, zone-based layout.
 *
 * Phase 2 Redesign: Removed activeActivityTab (no tabs in unified surface).
 * Added activityBarSize (thin/normal/expanded) and openActivityBar(action).
 *
 * Phase 5: Added toggleActivityBar (header toggle), auto-adopt patient route context.
 * Phase 6: Added activityBarSearchQuery for InputRow search mode on /patients.
 *
 * Phase 7: Added per-page activityBarDismissed map for open/closed persistence.
 *
 * See: docs/features/infrastructure/app-shell.md#state-management
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { updateUserSettings } from "../utils/firestoreService";

const AppShellContext = createContext();

/**
 * Derive initial sidebar mode from legacy `sidebarOpen` boolean
 * and current viewport width.
 *   sidebarOpen: false  → 'icon'
 *   sidebarOpen: true   → 'full'
 *   (undefined)         → breakpoint-based default
 */
function deriveInitialSidebarMode(sidebarOpen) {
    const width = window.innerWidth;
    if (sidebarOpen === false) return "icon";
    if (sidebarOpen === true) return "full";
    // Breakpoint defaults
    if (width < 768) return "thin";
    if (width < 1024) return "icon";
    return "full";
}

export const AppShellProvider = ({ children, userId, initialSidebarOpen, initialActivityBarPosition, initialActivityBarSize, initialActivityBarDismissed }) => {
    const [sidebarMode, setSidebarModeRaw] = useState(() =>
        deriveInitialSidebarMode(initialSidebarOpen)
    );
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    // Activity Bar state (Phase 2 Redesign — unified zone-based surface)
    // Clamp any persisted "right" value to "top" (right position removed)
    const validPositions = new Set(["top", "bottom"]);
    const [activityBarPosition, setActivityBarPositionRaw] = useState(
        validPositions.has(initialActivityBarPosition) ? initialActivityBarPosition : "top"
    );
    const [activityBarOpen, setActivityBarOpen] = useState(false);
    // Legacy: activityBarSize kept for backward compat but fixed to "normal"
    const activityBarSize = "normal";
    const [pendingAction, setPendingAction] = useState(null); // action triggered by header icon: 'record' | 'upload' | 'export' | null
    // Track if bar was opened implicitly (dashboard button, record action) vs explicitly (header toggle)
    // When true, the bar should auto-close when the activity (recording/upload) finishes.
    const [activityBarImplicitOpen, setActivityBarImplicitOpen] = useState(false);

    // Per-page dismissed state: { contextKey: true } — persisted to Firestore
    const [activityBarDismissed, setActivityBarDismissed] = useState(
        () => initialActivityBarDismissed || {}
    );
    // Debounce timer ref for batching Firestore writes
    const dismissedWriteTimer = useRef(null);

    // Phase 6: Search query from InputRow in search mode (drives PatientsList filtering)
    const [activityBarSearchQuery, setActivityBarSearchQuery] = useState("");

    // Dashboard patient selection (Clinical Day View)
    const [selectedPatientId, setSelectedPatientIdRaw] = useState(null);
    const [selectedPatientName, setSelectedPatientNameRaw] = useState(null);

    // Persist sidebar mode changes to Firestore
    const setSidebarMode = useCallback(
        (mode) => {
            setSidebarModeRaw(mode);
            if (userId) {
                // Write both new key and legacy key for backward compat
                updateUserSettings(userId, {
                    sidebarMode: mode,
                    sidebarOpen: mode === "full",
                });
            }
        },
        [userId]
    );

    // Desktop/tablet sidebar toggle. The sidebar and this control are hidden
    // below 768px; mobile navigation lives in HeaderUserMenu.
    const toggleSidebar = useCallback(() => {
        setSidebarMode(sidebarMode === "full" ? "icon" : "full");
    }, [sidebarMode, setSidebarMode]);

    // Auto-close user menu after timeout
    useEffect(() => {
        if (userMenuOpen) {
            const timer = setTimeout(() => setUserMenuOpen(false), 5000);
            return () => clearTimeout(timer);
        }
    }, [userMenuOpen]);

    // Update --sidebar-width CSS variable when mode changes
    useEffect(() => {
        const widths = { full: "200px", icon: "48px", thin: "32px" };
        document.documentElement.style.setProperty(
            "--sidebar-width",
            widths[sidebarMode] || "200px"
        );
    }, [sidebarMode]);

    // Persist activity bar position to Firestore
    const setActivityBarPosition = useCallback(
        (position) => {
            setActivityBarPositionRaw(position);
            if (userId) {
                updateUserSettings(userId, { activityBarPosition: position });
            }
        },
        [userId]
    );

    /**
     * Open the activity bar and set a pending action (e.g. 'record', 'upload', 'export').
     * If already open, just update the pending action.
     * Called by header icons: openActivityBar('record'), openActivityBar('upload'), etc.
     * 
     * @param {string|null} action - The action to trigger (record, upload, export)
     * @param {Object} options - Optional settings
     * @param {boolean} options.explicit - If true, bar stays open after activity completes (header buttons).
     *                                     If false/undefined, bar auto-closes when done (dashboard buttons).
     */
    const openActivityBar = useCallback(
        (action = null, options = {}) => {
            setPendingAction(action);
            // If opening with an action, determine if it should auto-close when done
            // Header buttons pass explicit: true, dashboard navigation doesn't
            if (action && !options.explicit) {
                setActivityBarImplicitOpen(true);
            } else if (options.explicit) {
                setActivityBarImplicitOpen(false);
            }
            if (!activityBarOpen) {
                setActivityBarOpen(true);
            }
        },
        [activityBarOpen]
    );

    /**
     * Clear pending action without closing the activity bar.
     * Used after an action has been consumed (e.g. recording started).
     */
    const clearPendingAction = useCallback(() => {
        setPendingAction(null);
        sessionStorage.removeItem("pendingAction");
    }, []);

    /**
     * Close activity bar (clear pending action too).
     * If a page context is provided, mark it as dismissed so the bar
     * won't auto-open on that page until the user reopens it.
     */
    const closeActivityBar = useCallback((context) => {
        setActivityBarOpen(false);
        setPendingAction(null);
        setActivityBarImplicitOpen(false);
        sessionStorage.removeItem("pendingAction");
        if (context) {
            setActivityBarDismissed(prev => {
                const next = { ...prev, [context]: true };
                // Debounced persist to Firestore
                if (userId) {
                    clearTimeout(dismissedWriteTimer.current);
                    dismissedWriteTimer.current = setTimeout(() => {
                        updateUserSettings(userId, { activityBarDismissed: next });
                    }, 500);
                }
                return next;
            });
        }
    }, [userId]);

    /**
     * Close activity bar only if it was opened implicitly (via action button from dashboard/record).
     * Called when an activity completes (recording stops, upload submitted, etc.).
     * If the user explicitly opened the bar via header toggle, this is a no-op.
     */
    const closeIfImplicit = useCallback(() => {
        if (activityBarImplicitOpen) {
            setActivityBarOpen(false);
            setPendingAction(null);
            setActivityBarImplicitOpen(false);
            sessionStorage.removeItem("pendingAction");
        }
    }, [activityBarImplicitOpen]);

    /**
     * Toggle activity bar open/closed (Phase 5 — header toggle button).
     * Opens to last-used size; if already open, closes.
     * Unlike openActivityBar(action), does NOT set a pending action.
     * Accepts optional context to persist dismissed state on close,
     * or clear it on open.
     * Marks bar as explicitly opened, so it won't auto-close when activity finishes.
     */
    const toggleActivityBar = useCallback((context) => {
        if (activityBarOpen) {
            closeActivityBar(context);
        } else {
            // User explicitly opens — clear dismissed for this context
            // and mark as explicit (won't auto-close)
            setActivityBarImplicitOpen(false);
            if (context) {
                setActivityBarDismissed(prev => {
                    const next = { ...prev };
                    delete next[context];
                    if (userId) {
                        clearTimeout(dismissedWriteTimer.current);
                        dismissedWriteTimer.current = setTimeout(() => {
                            updateUserSettings(userId, { activityBarDismissed: next });
                        }, 500);
                    }
                    return next;
                });
            }
            setActivityBarOpen(true);
        }
    }, [activityBarOpen, closeActivityBar, userId]);

    // Dashboard patient selection helpers
    const setSelectedPatient = useCallback((id, name) => {
        setSelectedPatientIdRaw(id);
        setSelectedPatientNameRaw(name || null);
    }, []);

    const clearSelectedPatient = useCallback(() => {
        setSelectedPatientIdRaw(null);
        setSelectedPatientNameRaw(null);
    }, []);

    // cycleActivityBarSize removed — replaced by chat collapse toggle in ActivityBar.js



    // Update --activity-bar-height CSS variable for top/bottom positions
    useEffect(() => {
        if ((activityBarPosition === "top" || activityBarPosition === "bottom") && activityBarOpen) {
            document.documentElement.style.setProperty("--activity-bar-height", "auto");
        } else {
            document.documentElement.style.setProperty("--activity-bar-height", "0px");
        }
    }, [activityBarPosition, activityBarOpen]);

    const value = {
        // Sidebar
        sidebarMode,
        setSidebarMode,
        toggleSidebar,
        userMenuOpen,
        setUserMenuOpen,
        // Activity Bar
        activityBarPosition,
        setActivityBarPosition,
        activityBarOpen,
        activityBarSize,
        pendingAction,
        clearPendingAction,
        openActivityBar,
        closeActivityBar,
        closeIfImplicit,
        activityBarImplicitOpen,
        toggleActivityBar,
        activityBarDismissed,
        // Dashboard patient selection
        selectedPatientId,
        selectedPatientName,
        setSelectedPatient,
        clearSelectedPatient,
        // Phase 6: Search mode
        activityBarSearchQuery,
        setActivityBarSearchQuery,
    };

    return (
        <AppShellContext.Provider value={value}>
            {children}
        </AppShellContext.Provider>
    );
};

export const useAppShellContext = () => {
    const ctx = useContext(AppShellContext);
    if (!ctx) {
        throw new Error("useAppShellContext must be used within AppShellProvider");
    }
    return ctx;
};
